import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { IsTouchOnlyEnvironment } from '../../../helper/mobile';
import { HelpText } from '../../../model/help-text';
import { InputControlBase } from '../../forms/input-control.base';
import { AstralLabelOverflowDirective } from '../astral-label-overflow.directive';
import { AstralPopoverComponent } from '../astral-popover/astral-popover.component';
import { shouldShowAstralValidationError } from '../astral-validation-state';
import { AstralFieldAccessibilityDirective, getAstralFieldErrorId, getAstralFieldHelpId } from '../astral-field-accessibility.util';

@Component({
  selector: 'astral-checkbox',
  standalone: true,
  imports: [TranslatePipe, AstralPopoverComponent, AstralLabelOverflowDirective, AstralFieldAccessibilityDirective],
  template: `
    <div
      class="astral-checkbox"
      [class.astral-checkbox--compact]="variant === 'compact'"
      [class.astral-checkbox--disabled]="resolvedDisabled"
      [class.astral-checkbox--field]="variant === 'field'"
      [class.astral-checkbox--focused]="isFocused"
      [class.astral-checkbox--invalid]="resolvedInvalid"
      [class.astral-checkbox--round]="resolvedShape === 'round'"
      [class.astral-checkbox--readonly]="resolvedReadonly"
      [class.astral-checkbox--square]="resolvedShape === 'square'"
      [class.astral-checkbox--with-subtitle]="showCaption && !!subtitle"
    >
      <div class="astral-checkbox__layout">
        @if (resolvedHelpText) {
          <astral-popover class="astral-checkbox__help" [helpText]="resolvedHelpText" [contentId]="helpId" [inline]="true"></astral-popover>
        }

        <label class="astral-checkbox__label" [attr.for]="resolvedInputId">
          @if (showCaption) {
            <span class="astral-checkbox__content">
              <span
                astralLabelOverflow
                class="astral-checkbox__caption"
                [id]="resolvedInputId + '-label'"
                (astralLabelOverflowChange)="labelTruncated = $event"
              >{{ resolvedLabel | translate }}</span>
              @if (subtitle) {
                <span class="astral-checkbox__subtitle" [id]="resolvedInputId + '-subtitle'">{{ subtitle | translate }}</span>
              }
            </span>
          }

          <span class="astral-checkbox__control">
            <input
              #input
              astralFieldAccessibility
              [astralFieldControl]="control"
              [astralFieldHelpText]="helpText"
              [astralFieldInputId]="resolvedInputId"
              [astralFieldDescribedBy]="showCaption && subtitle ? resolvedInputId + '-subtitle' : null"
              [attr.aria-labelledby]="showCaption && subtitle ? resolvedInputId + '-label' : null"
              class="astral-checkbox__native"
              type="checkbox"
              [attr.id]="resolvedInputId"
              [attr.name]="resolvedInputName"
              [checked]="resolvedChecked"
              [disabled]="resolvedDisabled ? true : null"
              [required]="control?.required ? true : null"
              (blur)="handleBlur()"
              (change)="handleChange($event)"
              (click)="handleClick($event)"
              (focus)="handleFocus()"
            />

            <span class="astral-checkbox__box" aria-hidden="true"></span>
          </span>
        </label>

        @if (labelTruncated) {
          <astral-popover
            class="astral-checkbox__label-overflow"
            [helpText]="resolvedLabel"
            [triggerIcon]="'ellipsis'"
            [triggerLabelKey]="labelOverflowTriggerLabelKey"
            [inline]="true"
          ></astral-popover>
        }

      </div>

      @if (resolvedInvalid && resolvedErrorMessage) {
        <div class="astral-checkbox__error" [attr.id]="errorId" role="alert">
          {{ resolvedErrorMessage.message | translate: resolvedErrorMessage.params }}
        </div>
      }
    </div>
  `,
  styleUrls: ['./astral-checkbox.component.scss'],
})
export class AstralCheckboxComponent extends InputControlBase<boolean> implements AfterViewInit {
  private static nextId = 0;
  private readonly uid = `astral-checkbox-${++AstralCheckboxComponent.nextId}`;

  constructor(private readonly cd: ChangeDetectorRef) {
    super();
  }

  @Input() checked = false;
  @Input() disabled = false;
  @Input() focused = false;
  @Input() inputId: string;
  @Input() inputName: string;
  @Input() label: string;
  @Input() readonly = false;
  @Input() shape: 'round' | 'square';
  @Input() showCaption = true;
  @Input() subtitle: string;
  @Input() span: number | 'full' = 1;
  @Input() variant: 'field' | 'compact' = 'field';

  @Output() readonly checkedChange = new EventEmitter<boolean>();

  @ViewChild('input') input: ElementRef<HTMLInputElement>;

  isFocused = false;
  labelTruncated = false;
  private originalStandaloneValue = false;
  private controlSubscription = new Subscription();

  @HostBinding('tabindex') tabindex = -1;

  @HostBinding('style.grid-column')
  get hostGridColumn(): string | null {
    if (this.span === 'full') {
      return '1 / -1';
    }

    if (!this.span || this.span <= 1) {
      return null;
    }

    return `span ${this.span}`;
  }

  get resolvedDisabled(): boolean {
    return this.control?.disabled ?? this.disabled;
  }

  override onControlSet(): void {
    super.onControlSet();
    this.controlSubscription.unsubscribe();
    this.controlSubscription = new Subscription();

    if (!this.control) {
      return;
    }

    this.controlSubscription.add(this.control.statusChanges.subscribe(() => this.cd.markForCheck()));
    this.controlSubscription.add(this.control.valueChanges.subscribe(() => this.cd.markForCheck()));
    this.controlSubscription.add(this.control.touchStateChanged?.subscribe(() => this.cd.markForCheck()));
    this.controlSubscription.add(this.control.validatorsChanged?.subscribe(() => this.cd.markForCheck()));
  }

  override ngOnDestroy(): void {
    this.controlSubscription.unsubscribe();
    super.ngOnDestroy();
  }

  get resolvedErrorMessage(): { message: string; params: any } {
    return this.errorMessage;
  }

  get resolvedHelpText(): string | null {
    const helpText = this.helpText ?? this.control?.helpText;
    if (!helpText) {
      return null;
    }

    if (typeof helpText === 'string') {
      return helpText;
    }

    return (helpText as HelpText).text ?? null;
  }

  get resolvedInputId(): string {
    return this.inputId ?? this.uid;
  }

  get errorId(): string {
    return getAstralFieldErrorId(this.resolvedInputId);
  }

  get helpId(): string {
    return getAstralFieldHelpId(this.resolvedInputId);
  }

  get resolvedInputName(): string {
    if (this.inputName) {
      return this.inputName;
    }

    const caption = (this.control?.caption ?? this.label)
      ?.toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    return caption || this.uid;
  }

  get resolvedInvalid(): boolean {
    return shouldShowAstralValidationError(this.control);
  }

  get resolvedLabel(): string {
    return this.control?.caption ?? this.label ?? '';
  }

  get labelOverflowTriggerLabelKey(): string {
    return IsTouchOnlyEnvironment() ? 'COMMON.TAP_TO_SHOW_FULL_FIELD_NAME' : 'COMMON.CLICK_TO_SHOW_FULL_FIELD_NAME';
  }

  get resolvedChecked(): boolean {
    return !!(this.control ? this.control.value : this.checked);
  }

  get resolvedReadonly(): boolean {
    return this.control?.isReadonly ?? this.readonly;
  }

  get resolvedShape(): 'round' | 'square' {
    if (this.shape) {
      return this.shape;
    }

    return this.variant === 'compact' ? 'square' : 'round';
  }

  @HostListener('focus')
  focusHost() {
    this.input?.nativeElement.focus();
  }

  ngAfterViewInit(): void {
    if (this.focused) {
      this.input?.nativeElement.focus();
      this.isFocused = true;
      this.cd.detectChanges();
    }
  }

  handleBlur(): void {
    this.isFocused = false;
    this.control?.markAsTouched();
    this.onblur();

    if (!this.control && this.originalStandaloneValue !== this.resolvedChecked) {
      this.valueChange.emit({
        formSnapshot: null,
        newValue: this.resolvedChecked,
        previousValue: this.originalStandaloneValue,
      });
    }
  }

  handleChange(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    const previousValue = this.resolvedChecked;

    if (this.control && this.control.value !== checked) {
      this.control.setValue(checked);
      this.control.markAsDirty();
    }

    this.checkedChange.emit(checked);
    this.valueChange.emit({
      formSnapshot: this.control?.parent ? Object.assign({}, this.control.parent.value) : null,
      newValue: checked,
      previousValue,
    });
  }

  handleClick(event: Event): void {
    if (!this.resolvedReadonly) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
  }

  handleFocus(): void {
    this.isFocused = true;
    this.originalStandaloneValue = this.resolvedChecked;
    this.onfocus();
  }
}
